import Character from '@/domain/Character'
import Action from '@/domain/Action'
import database from '@/utils/database'
import Item from '@/domain/Item'
import { byTierType } from '@/utils/orderings'
import Vue from 'vue'

const processLoadedData = ({characterEquipment, characterInventories, profileInventory}) => {
  const processedCharacterItems = Object.entries(characterEquipment)
    .concat(Object.entries(characterInventories))
    .reduce((acc, [characterHash, data]) => {
      return acc.concat(data.items.map((itemData) => new Item(itemData, characterHash)))
    }, [])
  const processedProfileInventory = profileInventory.items.map((itemData) => new Item(itemData, 'VAULT'))
  const result = processedCharacterItems
    .concat(processedProfileInventory)
    .filter((item) => item.enabled)
  return result
}

export const load = ({commit}, {membershipId, membershipType}) => {
  return Vue.http.get(`platform/Destiny2/${membershipType}/Profile/${membershipId}/?components=102,200,201,205,300,302,304,305,307,308`) // ,301,302,303,304,305,306,307,308
    .then(({data}) => {
      const characters = Object.values(data.Response.characters.data).map(data => new Character(data))
      commit('setCharacters', {characters: characters})
      const lazyItems = () => {
        const items = processLoadedData({
          characterEquipment: data.Response.characterEquipment.data,
          characterInventories: data.Response.characterInventories.data,
          profileInventory: data.Response.profileInventory.data
        })
        if (process.env.NODE_ENV !== 'production') {
          window.items = items
        }
        commit('setItems', {items})
      }
      const itemComponents = Object.entries(data.Response.itemComponents).reduce((acc, [name, {data}]) => {
        acc[name] = data
        return acc
      }, {})
      commit('setItemComponents', {itemComponents})
      commit('setLoaded', {loaded: true})
      return lazyItems
    })
}

export const reload = ({commit, rootGetters, rootState}) => {
  const {membershipId, membershipType} = rootState.destiny.currentDestinyMembership
  commit('destiny/setLoading', {loading: 'Reloading inventory'}, {root: true})

  return Vue.http.get('platform/Destiny2/' + membershipType + '/Profile/' + membershipId + '/?components=102,201,205,300,302,304,305,307,308') // ,301,302,303,304,305,306,307,308
    .then(({data}) => {
      const items = processLoadedData({
        characterEquipment: data.Response.characterEquipment.data,
        characterInventories: data.Response.characterInventories.data,
        profileInventory: data.Response.profileInventory.data
      })
      const itemComponents = Object.entries(data.Response.itemComponents).reduce((acc, [name, {data}]) => {
        acc[name] = data
        return acc
      }, {})

      return () => {
        commit('setItemComponents', {itemComponents})
        commit('setItems', {items})
        commit('destiny/setLoading', {loading: false}, {root: true})
      }
    })
    .catch((e) => {
      commit('destiny/setLoading', {loading: false}, {root: true})
      throw e
    })
}

export const transferItem = ({state, getters, dispatch}, {item, character}) => {
  const targetBucket = character.isVault ? database.getInventoryBucket(character.bucketHash) : item.bucket
  const targetBucketItems = getters.itemsByBucketForCurrentCharacter({bucket: targetBucket})
  if (item.equipped) {
    item.unEquip()
  }
  if (targetBucket.itemCount > targetBucketItems.length + 1) {
    return dispatch('transferItemToCharacter', {item, character})
  } else if (!character.isVault) {
    const toGetRid = targetBucketItems.filter(item => !item.favorited && !item.equipped).sort(byTierType).pop()
    return dispatch('transferItemToCharacter', {item: toGetRid, character: item.character})
      .then(() => {
        return dispatch('transferItemToCharacter', {item, character})
      })
  } else {
    throw new Error('inventory full')
    /*
    const items = getters.itemsByTypeForCurrentCharacter({type: item.type})
      .sort(byTierType)
    if (state.selectedCharacter !== 'VAULT' && items.length === item.type.itemCount) {
      if (item.character.key === 'VAULT' || item.siblings.length < item.type.itemCount) {
        dispatch('transferItemToCharacter', {item: items[0], characterId: item.character.key})
      }
    }
    dispatch('transferItemToCharacter', {item, characterId: state.selectedCharacter})
    */
  }
}

export const transferItemToCharacter = ({state, getters, dispatch}, {item, character}) => {
  if (character.isVault) {
    return dispatch('transferItemToVault', {item})
  } else if (item.character.isVault) {
    return dispatch('transferItemFromVault', {item, character})
  } else {
    return dispatch('transferItemToVault', {item, character})
      .then(() => {
        dispatch('transferItemFromVault', {item, character})
      })
  }
}

export const transferItemFromVault = ({dispatch, rootState, commit}, {item, character}) => {
  // const characterIndex = state.characters.map((character) => character.characterId).indexOf(characterId)
  commit('updateItemCharacter', {item, newCharacterId: character.characterId})
  // commit('updateItemCharacterIndex', {item: item, newCharacterIndex: characterIndex})

  return dispatch('queue/pushAction', {
    action: new Action({
      label: '<i class="material-icons">file_upload</i> Transfer <strong>' + item.name + '</strong> from vault',
      url: 'Platform/Destiny2/Actions/Items/TransferItem/',
      method: 'post',
      payload: {
        itemReferenceHash: item.data.itemHash,
        stackSize: 0,
        transferToVault: false,
        itemId: item.data.itemInstanceId,
        characterId: character.characterId,
        membershipType: rootState.destiny.currentDestinyMembership.membershipType
      }
    })
  }, {root: true})
}

export const transferItemToVault = ({dispatch, rootState, commit, state}, {item}) => {
  const oldCharacterIndex = item.character.characterId
  commit('updateItemCharacter', {item, newCharacterId: 'VAULT'})
  window.item = item
  return dispatch('queue/pushAction', {
    action: new Action({
      label: '<i class="material-icons">file_download</i> Transfer <strong>' + item.name + '</strong> to vault',
      url: 'Platform/Destiny2/Actions/Items/TransferItem/',
      method: 'post',
      payload: {
        itemReferenceHash: item.data.itemHash,
        stackSize: item.data.itemInstanceId ? 0 : item.data.quantity,
        transferToVault: true,
        itemId: item.data.itemInstanceId,
        characterId: oldCharacterIndex !== 'INVENTORY' ? oldCharacterIndex : state.characters[0].characterId,
        membershipType: rootState.destiny.currentDestinyMembership.membershipType
      }
    })
  }, {root: true})
}

export const equipItem = ({dispatch, state, commit, rootState}, {item}) => {
  console.assert(item.canEquip, 'should be allowed to equip')
  const equipped = item.siblings.find(sibling => sibling.equipped)
  commit('setUnequipped', {item: equipped})
  commit('setEquipped', {item: item})

  return dispatch('queue/pushAction', {
    action: new Action({
      label: '<i class="material-icons">bookmark</i> Equipping ' + item.name,
      url: 'Platform/Destiny2/Actions/Items/EquipItem/',
      method: 'post',
      payload: {
        itemId: item.data.itemInstanceId,
        characterId: item.character.characterId,
        membershipType: rootState.destiny.currentDestinyMembership.membershipType
      }
    })
  }, {root: true})
}

export const unEquipItem = ({dispatch}, {item}) => {
  const candidates = item.siblings.filter(sibiling => sibiling.canEquip && sibiling.key !== item.key)
    .sort(byTierType)
  if (candidates.length) {
    return dispatch('equipItem', {item: candidates[0]})
  } else {
    throw Error('Cannot unequip ' + item.name + ', there is nothing to equip')
  }
}

export const lockItem = ({dispatch, commit, rootState}, {item}) => {
  console.assert(item.canToggleLock, 'should be allowed to toggle lock')
  commit('setLocked', {item: item})

  return dispatch('queue/pushAction', {
    action: new Action({
      label: '<i class="material-icons">lock</i> Locking ' + item.name,
      url: 'Platform/Destiny2/Actions/Items/SetLockState/',
      method: 'post',
      payload: {
        itemId: item.data.itemInstanceId,
        characterId: item.character.characterId !== 'VAULT' ? item.character.characterId : null,
        membershipType: rootState.destiny.currentDestinyMembership.membershipType,
        state: 1
      }
    })
  }, {root: true})
}

export const unLockItem = ({dispatch, rootState, commit}, {item}) => {
  commit('setUnLocked', {item: item})
  return dispatch('queue/pushAction', {
    action: new Action({
      label: '<i class="material-icons">lock_open</i> Unlocking ' + item.name,
      url: 'Platform/Destiny2/Actions/Items/SetLockState/',
      method: 'post',
      payload: {
        itemId: item.data.itemInstanceId,
        characterId: item.character.characterId !== 'VAULT' ? item.character.characterId : null,
        membershipType: rootState.destiny.currentDestinyMembership.membershipType,
        state: 0
      }
    })
  }, {root: true})
}



// WEBPACK FOOTER //
// ./src/store/destiny/profile/actions.js